// Round icon button for the video tile overlay. Self-contained: caller passes
// `kind` and `state` and we render the matching inline SVG. CSS in
// Conversation.module.css (`.videoControl`) paints the color via currentColor.

import type { JSX } from 'react';

export type VideoControlKind = 'mic' | 'cam' | 'share' | 'hangup';

interface Props {
  kind: VideoControlKind;
  state: 'on' | 'off';
  onClick: () => void;
  /** CSS module-resolved class name (passed from the calling component). */
  className: string | undefined;
  /** Disabled when we don't yet have a local stream to toggle. */
  disabled?: boolean;
}

export function VideoControl({ kind, state, onClick, className, disabled }: Props) {
  const label = ariaFor(kind, state);
  return (
    <button
      type="button"
      className={className}
      data-kind={kind}
      data-state={state}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {renderIcon(kind, state)}
    </button>
  );
}

function ariaFor(kind: VideoControlKind, state: 'on' | 'off'): string {
  switch (kind) {
    case 'mic':
      return state === 'on' ? 'Mute microphone' : 'Unmute microphone';
    case 'cam':
      return state === 'on' ? 'Turn camera off' : 'Turn camera on';
    case 'share':
      return state === 'on' ? 'Stop sharing screen' : 'Share screen';
    case 'hangup':
      return 'End call';
  }
}

function renderIcon(kind: VideoControlKind, state: 'on' | 'off'): JSX.Element {
  const strike =
    state === 'off' ? (
      <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    ) : null;
  switch (kind) {
    case 'mic':
      return (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="9" y="3" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0" />
          <line x1="12" y1="18" x2="12" y2="22" />
          {strike}
        </svg>
      );
    case 'cam':
      return (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="2" y="6" width="14" height="12" rx="2" />
          <path d="M22 7l-6 5 6 5z" />
          {strike}
        </svg>
      );
    case 'share':
      return (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <rect x="2" y="4" width="20" height="13" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
          {state === 'on' ? <path d="M12 7v6m0-6 -3 3m3-3 3 3" /> : null}
        </svg>
      );
    case 'hangup':
      return (
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path
            d="M5 11.2c0-1.3.7-2.5 1.9-3.1L9.6 6.8c.7-.4 1.6-.2 2 .5l1.1 2c.3.5.2 1.2-.2 1.7l-1.1 1.2a9 9 0 0 0 4.4 4.4l1.2-1.1c.5-.4 1.2-.5 1.7-.2l2 1.1c.7.4.9 1.3.5 2l-1.3 2.7c-.6 1.2-1.8 1.9-3.1 1.9C10.2 23 1 13.8 1 11.2z"
            transform="rotate(135 12 12)"
          />
        </svg>
      );
  }
}
